<template>
  <v-row no-gutters>
    <v-col cols="12">
      <v-skeleton-loader v-if="$store.state.invoices.retrieveLoader" transition="fade-transition" type="table" />
      <template v-else>
        <v-col v-if="!invoicesList.length" class="text-center py-5">
          <v-icon class="d-block" color="secondary" size="70">mdi-file</v-icon>
          <span class="d-block subtitle-1">No existen documentos</span>
          <v-btn class="my-5" v-if="showCreate" :to="{name: 'InvoiceCreate'}" outlined>Crear un documento</v-btn>
        </v-col>
        <template v-else>
          <v-data-table :headers="headers" :items="invoicesList" :items-per-page="pagination" hide-default-header disable-sort mobile-breakpoint hide-default-footer @click:row="(invoicesList) => { hasLink ? $router.push({name: invoicesList.status === 'draft' ? 'InvoiceUpdate' : 'InvoiceRetrieve', params: {id: invoicesList.id}}) : ''}" style="cursor: pointer">
            <!-- header -->
            <template v-slot:header="{ props: { headers } }">
              <VTableHeaders :headers="headers" @sort="setSort" />
            </template>
            <!-- end header -->
            <template v-slot:[`item.customer`]="{item}">
              <div class="text-left py-4">
                <v-menu open-on-hover right offset-x :nudge-width="160" transition="slide-x-transition">
                  <template v-slot:activator="{on}">
                    <div v-on="on">
                      <span class="text--secondary font-weight-medium">{{item.customer.name}}</span>
                    </div>
                  </template>
                  <v-card class="pa-0">
                    <v-subheader class="font-weight-medium background">{{item.customer.name}}</v-subheader>
                    <v-card-text class="pa-0">
                      <v-row class="mx-0">
                        <v-col cols="12" class="pb-0">
                          <span class="text--secondary font-weight-medium">Facturación</span>
                        </v-col>
                        <v-col cols="4">
                          <span class="d-block">Total</span>
                          <span class="d-block">$ {{item.customer.aggregations.sales | number}}</span>
                        </v-col>
                        <v-col cols="4">
                          <span class="d-block">{{currentMonth | capitalize}}</span>
                          <span class="d-block">$ {{item.customer.aggregations.sales_current_period | number}}</span>
                        </v-col>
                        <v-col cols="4">
                          <span class="d-block">Balance</span>
                          <span class="d-block">$ {{item.customer.aggregations.balance | number}}</span>
                        </v-col>
                      </v-row>
                    </v-card-text>
                    <v-divider class="mx-3" />
                    <v-card-actions>
                      <v-spacer />
                      <v-btn :to="{name: 'CustomerRetrieveUpdate', params: {id: item.customer.id}}" text color="primary">Ver cliente <v-icon small right>mdi-arrow-right</v-icon></v-btn>
                      <v-spacer />
                    </v-card-actions>
                  </v-card>
                </v-menu>
              </div>
            </template>
            <template v-slot:[`item.status`]="{item}">
              <v-row align="center" justify="end">
                <v-menu open-on-hover right offset-x :nudge-width="100" transition="slide-x-transition" v-if="item.subscription">
                  <template v-slot:activator="{on}">
                    <v-icon v-on="on" color="secondary">mdi-autorenew</v-icon>
                  </template>
                  <v-card>
                    <v-card-text>
                      <span>Esta factura fue creada por una suscripción</span>
                    </v-card-text>
                    <v-divider class="mx-4" />
                    <v-card-actions>
                      <v-spacer />
                      <v-btn text :to="{name: 'SubscriptionRetrieve', params: {id: item.subscription}}" color="primary">Ver suscripción<v-icon small right>mdi-arrow-right</v-icon></v-btn>
                      <v-spacer />
                    </v-card-actions>
                  </v-card>
                </v-menu>
                <div class="text-right" v-if="showStatus">
                  <v-chip v-if="item.status === 'draft'" class="mx-1" color="defaultGrey" text-color="secondary" small>Borrador</v-chip>
                  <v-chip v-else-if="item.status === 'finalized' && item.due_date > today" class="mx-1" color="defaultGrey" text-color="secondary" small>Pendiente de pago</v-chip>
                  <v-chip v-else-if="item.status === 'finalized' && item.due_date <= today" class="mx-1" color="defaultGrey" text-color="secondary" small>Vencido</v-chip>
                  <v-chip v-else-if="item.status === 'paid'" class="mx-1" color="defaultGrey" text-color="secondary" small>Pagado<v-icon class="ml-1" x-small>mdi-check</v-icon></v-chip>
                  <v-chip v-else-if="item.status === 'cancelled'" class="mx-1" color="defaultGrey" text-color="secondary" small>Tiene nota de crédito<v-icon class="ml-1" x-small>mdi-close</v-icon></v-chip>
                  <v-chip v-else-if="item.status === 'uncollectible'" class="mx-1" color="defaultGrey" text-color="secondary" small>Incobrable</v-chip>
                  <v-chip v-else-if="item.status === 'pending'" class="mx-1" color="defaultGrey" text-color="secondary" small>Pendiente<v-icon class="ml-1" x-small>mdi-clock</v-icon></v-chip>
                </div>
              </v-row>
            </template>
            <template v-slot:[`item.document`]="{item}">
              <div class="text-left py-2">
                <v-menu open-on-hover right offset-x :nudge-width="160" transition="slide-x-transition">
                  <template v-slot:activator="{on}">
                    <div v-on="on">
                      <span class="d-block">{{item.document.name}}</span>
                      <span class="d-block body-2 text--secondary font-weight-medium" v-if="!['draft', 'pending'].includes(item.status)">Nº {{item.number}}</span>
                    </div>
                  </template>
                  <v-card>
                    <v-subheader class="font-weight-medium background">{{item.document.name}}</v-subheader>
                    <v-card-text>
                      <v-list-item class="px-0">
                        <v-list-item-title class="text--secondary font-weight-medium">Productos</v-list-item-title>
                      </v-list-item>
                      <template v-if="item.lines.length">
                        <v-list-item class="px-0 my-1" v-for="p in item.lines.slice(0, 3)" :key="p.id">
                          <v-list-item-title>
                            <span class="d-block">{{p.name}}</span>
                            <span class="d-block font-italic text--disabled">{{p.quantity}} unidades</span>
                          </v-list-item-title>
                          <v-list-item-subtitle class="text-right">{{p.amount | currency(item.currency)}} {{item.currency}}</v-list-item-subtitle>
                        </v-list-item>
                      </template>
                      <v-list-item class="px-0 my-1" v-if="item.lines.length > 3">
                        <v-list-item-title>y otros {{item.lines.length - 3}} productos</v-list-item-title>
                      </v-list-item>
                    </v-card-text>
                    <v-divider class="mx-4" />
                    <v-card-actions>
                      <v-spacer />
                      <v-btn :to="{name: item.status === 'draft' ? 'InvoiceUpdate' : 'InvoiceRetrieve', params: {id: item.id}}" text color="primary">Ver detalle <v-icon small right>mdi-arrow-right</v-icon></v-btn>
                       <v-spacer />
                    </v-card-actions>
                  </v-card>
                </v-menu>
              </div>
            </template>
            <template v-slot:[`item.date`]="{item}">
              <span>{{item.date | date}}</span>
            </template>
            <template v-slot:[`item.due_date`]="{item}">
              <span v-if="item.due_date">{{item.due_date | date}}</span>
              <span v-else>---</span>
            </template>
            <template v-slot:[`item.total`]="{item}">
              <div class="text-right">
                <span class="body-2 text--secondary font-weight-medium">{{item.total | currency(item.currency)}}</span>
                <span class="ml-1">{{item.currency}}</span>
              </div>
            </template>
          </v-data-table>
          <template v-if="showPagination || showAll">
            <v-divider />
            <v-row class="mx-0" align="center">
              <v-col cols="12" class="text-center" v-if="showAll">
                <v-btn :to="{name: 'InvoiceList', query: showCriteria}" outlined color="secondary">Ver todas las facturas</v-btn>
              </v-col>
              <template v-if="showPagination">
                <v-col cols="6">
                  <span class="body-2 text--secondary">{{count}} registros</span>
                </v-col>
                <v-col cols="6" class="text-right">
                  <v-pagination v-model="page" :length="totalPages" total-visible="6" />
                </v-col>
              </template>
            </v-row>
          </template>
        </template>
      </template>
    </v-col>
  </v-row>
</template>
<script>
import { mapState } from 'vuex'
import VTableHeaders from '@/components/VTable/VTableHeaders'
import ListViewMixin from '@/mixins/ListViewMixin'
export default {
  components: {
    VTableHeaders
  },
  mixins: [
    ListViewMixin
  ],
  data: () => ({
    headers: [
      { text: 'Cliente', value: 'customer', class: 'font-weight-bold', sortable: true },
      { text: '', value: 'status' },
      { text: 'Documento', value: 'document' },
      { text: 'Fecha', value: 'date', align: 'end', sortable: true },
      { text: 'Vencimiento', value: 'due_date', align: 'end', sortable: true },
      { text: 'Total', value: 'total', align: 'end', sortable: true }
    ]
  }),
  computed: mapState({
    count: state => state.invoices.invoicesCount,
    invoicesList: state => state.invoices.invoicesList
  })
}
</script>